import { useCallback, useEffect, useMemo, useState } from "react";

import { CarrierService } from "$types";
import { Services } from "$utils/typeConstants";

import useAuth from "./useAuth";
import { useList } from "$hooks/useList";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const useCarrierServiceList = () => {
  const { token } = useAuth();

  // Use the new useList implementation without cacheKey
  const {
    data: carrierServices = [],
    refetch: fetchCarrierServices,
    isLoading: isFetching,
  } = useList<CarrierService & { id: string | number }>("carrierServices", {
    // Move service inside options
    service: Services.SHIPPING_API,
    // Pass token if needed
    ...(token ? { jwt: token } : {}),
    // Add additionalParams if needed
    additionalParams: {},
  });

  const [companies, setCompanies] = useState(null);

  // get companies
  const fetchCompanies = useCallback(async () => {
    if (carrierServices?.length > 0) {
      // Get all ids
      const listId = [
        ...new Set(
          carrierServices
            .map((carrierService: any) => carrierService.companyId)
            .flat()
            .filter((f) => f)
        ),
      ];

      if (listId.length === 0) return;

      const filters = {
        $inId: listId,
      };

      let companies;
      companies = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);

      if (!companies) {
        companies = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANIES, listId],
          () => CompanyServices.getCompanies(filters),
          { cacheTime: 5 * 60 * 1000 }
        );
        companies = companies.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          companies
        );
      }

      setCompanies(companies);
    }
  }, [carrierServices]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Merge carrier services with company data
  const carrierServicesWithCompanies = useMemo(
    () =>
      carrierServices.map((carrierService) => ({
        ...carrierService,
        company: carrierService.companyId
          ? (companies?.data || []).find(
              (company) => company._id === carrierService.companyId
            )
          : null,
      })),
    [carrierServices, companies]
  );

  return {
    carrierServices: carrierServicesWithCompanies,
    fetchCarrierServices,
    isFetching,
  };
};

export default useCarrierServiceList;
